// Electrical — per-template authored content. See ./types.ts for the content rule.
import type { TemplateSpecific } from "./types";

export const ELECTRICAL: Record<string, TemplateSpecific> = {
  "elec-consumer-unit": {
    whoFor:
      "This RAMS is for the electrician replacing a domestic or small-commercial consumer unit (fuse board) — usually because a client, landlord or principal contractor wants an upgrade to a board with RCD/RCBO protection and a certificate to follow. It suits the qualified electrician swapping an old board on an occupied or part-occupied property who needs to show their H&S manager or building control that the work is done dead, notified under Part P and proved safe before re-energising.",
    scope:
      "The scope covers isolating the existing installation, recording the existing circuits, removing the old consumer unit, mounting and wiring the new board, and carrying out the BS 7671 inspection and test sequence before re-energising. Work is carried out dead with the main switch off and, where the cut-out can be safely isolated, the supplier's fuse withdrawn under agreement. It does not cover live work on the supply side of the meter, meter changes, or DNO cut-out work, which are the supply company's responsibility.",
    legislation: [
      "Electricity at Work Regulations 1989 (regs 4 and 14)",
      "Building Regulations Approved Document P (notifiable domestic electrical work)",
      "BS 7671:2018+A2 (IET Wiring Regulations)",
      "HSG85 Electricity at Work: Safe Working Practices",
      "Control of Asbestos Regulations 2012 (pre-2000 boards and backing)",
    ],
    sequence: [
      "Record the existing circuit arrangement, label cables and check the earthing and bonding before disturbing the board.",
      "Switch off the main switch, withdraw or isolate the supply fuse by agreement with the supplier, and lock or cap the isolation so it cannot be restored.",
      "Prove the installation dead at the tails with an approved voltage indicator checked against a proving unit before and after.",
      "Remove the old consumer unit, checking the mounting surface and any backing board for asbestos before cutting or drilling.",
      "Mount and wire the new board, transferring circuits to the correct RCD/RCBO ways and confirming conductor sizes and terminations.",
      "Carry out the BS 7671 dead-test sequence, re-energise under control, complete live tests and issue the certificate and Part P notification.",
    ],
    hazards: [
      {
        hazard: "Electric shock from removing the old board before the installation is proved dead",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Switch off the main switch and isolate the supply fuse by agreement, then lock or cap so it cannot be restored",
          "Prove dead at the tails with a GS38 voltage indicator checked against a proving unit before and after",
          "Treat the meter tails and supply side as live at all times — they are not isolated by the main switch",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Disturbing asbestos in the backing board or fabric behind a board in a pre-2000 property",
        who: ["Operatives", "Occupants"],
        initial: { likelihood: 2, severity: 4 },
        controls: [
          "Check the age of the property and inspect the mounting surface before cutting or drilling",
          "Stop and arrange a sample or refurbishment survey if a suspect backing board or insulation is found",
          "Mount the new board on a fresh non-combustible backing rather than disturbing suspect material",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Re-energisation of the supply by an occupant or other trade while the board is open",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Keep the isolation under your sole control with a lock and a caution notice at the cut-out and meter position",
          "Tell occupants and other trades the power is off and must not be restored until you say so",
          "Re-prove dead at the point of work if you leave and return to the board",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Back-feed or a borrowed neutral keeping a supposedly dead circuit live",
        who: ["Operatives"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Prove dead on every conductor at the board, not just the incomer, before disconnecting",
          "Identify and separate any shared or borrowed neutrals between circuits before transferring them",
          "Confirm there is no alternative supply such as a generator, inverter or PV before starting",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "Insulating gloves rated for the working voltage when handling tails",
      "Arc-rated face protection for work at the board",
      "Safety footwear with toe protection",
      "Disposable mask and coveralls if suspect dust is disturbed at the mounting surface",
    ],
    competence: [
      "ECS card with the relevant electrician grade",
      "18th Edition BS 7671:2018+A2 qualification",
      "Inspection and testing competence (C&G 2391 or equivalent) for certification",
      "Registration with a Part P competent person scheme, or building control notification arranged",
    ],
    permits: [
      "Isolation and lock-off agreement with the supplier where the cut-out fuse is withdrawn",
      "Asbestos check or clearance before cutting into a suspect mounting surface",
      "Client agreement for the period the property will be without power",
    ],
    equipment: [
      "Lock-off kit and caution notices for the main switch and cut-out",
      "Approved voltage indicator and GS38 proving unit",
      "Multifunction installation tester for the BS 7671 sequence",
      "Insulated screwdrivers and a torque screwdriver for board terminations",
      "Temporary task lighting for working at an unlit board position",
    ],
    pcChecks: [
      "That the method proves dead at the tails and treats the supply side as live, rather than relying on the main switch alone",
      "That an asbestos check of the mounting surface is included for older properties before any cutting or drilling",
      "That re-energisation and live testing are a controlled step after a satisfactory dead-test sequence, with a named person certifying",
    ],
    faqs: [
      {
        q: "Can I isolate a consumer unit without the supplier withdrawing the cut-out fuse?",
        a: "The main switch isolates the consumer unit's outgoing circuits but not the meter tails or the supply side, which stay live throughout the job. Best practice is to arrange isolation of the cut-out fuse with the supply company so the tails can be worked on safely, and to treat anything upstream of the main switch as live if that is not possible. Pulling a supplier's fuse without agreement is not your equipment to work on, so the RAMS should set out who isolates the cut-out and how the tails are proved dead before the old board comes off.",
      },
      {
        q: "Why does this RAMS flag asbestos on a simple board change?",
        a: "Older consumer units are often mounted on a timber or insulating backing board, and the fabric behind them in pre-2000 properties can contain asbestos. Cutting, drilling or breaking that material out to fit a modern board can release fibres into an occupied home. The method therefore includes a check of the mounting surface before any disturbance and a stop point if suspect material is found, so a routine upgrade does not turn into an uncontrolled asbestos exposure.",
      },
    ],
  },
  "elec-installation": {
    whoFor:
      "This RAMS is for electrical contractors carrying out first and second fix installation work on a construction project — typically when the principal contractor has asked for your method statement before you start on site. It suits the qualified electrician and their team installing containment, cabling, accessories and the final consumer unit on a new build, fit-out or refurbishment. If your client's H&S manager wants evidence that you work dead and prove the installation safe before energising, this is the document they are looking for.",
    scope:
      "The scope covers a complete dead electrical installation: setting out, fixing containment and back boxes, pulling and terminating cables, installing accessories and boards, and the final connection at the distribution board. All work is carried out on circuits that are isolated and proved dead at point of work. The document does not cover live fault-finding, work on existing live installations, or energised testing beyond the prescribed BS 7671 inspection and test sequence carried out by a competent person. Energisation is a controlled, scheduled event covered by a separate authorisation.",
    legislation: [
      "Electricity at Work Regulations 1989 (regs 4 and 13)",
      "BS 7671:2018+A2 (IET Wiring Regulations)",
      "Construction (Design and Management) Regulations 2015",
      "HSG85 Electricity at Work: Safe Working Practices",
      "Provision and Use of Work Equipment Regulations 1998",
    ],
    sequence: [
      "Confirm the circuits to be worked on are de-energised, lock off at the board and apply a unique safety lock and caution tag for each operative.",
      "Set out and fix cable containment, trunking, conduit and back boxes to the agreed drawings, coordinating routes with other trades.",
      "Pull in and dress cables, leaving identified tails at boards and accessory positions; protect cable ends against damage and moisture.",
      "Terminate accessories, fix faceplates and make off the distribution board connections while the supply remains isolated and locked off.",
      "Carry out the BS 7671 dead-test sequence — continuity, insulation resistance and polarity — and record results on the schedule of test results.",
      "Hand over the locked-off installation for scheduled energisation; the lock-off is only removed under a controlled authorisation by the competent person.",
    ],
    hazards: [
      {
        hazard: "Inadvertent energisation of a circuit being worked on by another trade or the supply authority",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Apply individual locks and caution tags at the isolation point for every operative on the circuit",
          "Verify dead at the point of work with a proving unit before touching conductors",
          "Display the isolation register at the board so no other trade re-energises",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Falls from height fixing high-level containment and pulling cables on access equipment",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use a podium step or mobile tower inspected and tagged before use",
          "Maintain three points of contact and do not over-reach along containment runs",
          "Keep platforms clear of offcuts, cable drums and tools",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Drilling and cutting into walls, floors or ceilings striking buried existing services",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Check as-installed drawings and scan with a cable/pipe detector before penetrating any surface",
          "Where existing services may be present, isolate them and confirm dead before drilling",
          "Drill to a controlled depth and stop at any unexpected resistance",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Cuts and abrasions from stripping cable, dressing armour and handling sharp containment edges",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 2 },
        controls: [
          "Deburr cut trunking and conduit ends before handling",
          "Use cable strippers and the correct tool rather than knives where practicable",
          "Wear cut-resistant gloves when dressing SWA and pulling cable",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Eye injury from drilling debris and swarf when fixing back boxes and containment",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Wear impact-rated eye protection for all drilling and cutting",
          "Use dust extraction or on-tool capture where chasing masonry",
          "Position so debris falls away from the face and from others below",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
    ],
    ppe: [
      "Impact-rated safety glasses",
      "Cut-resistant gloves for cable and containment work",
      "Safety footwear with toe protection",
      "Head protection where overhead works or scaffold are present",
      "Knee pads for floor-level termination work",
    ],
    competence: [
      "ECS card with the relevant installation electrician grade",
      "18th Edition BS 7671:2018+A2 qualification",
      "Inspection and testing competence (C&G 2391 or equivalent) for the person signing test results",
      "Safe isolation training to Electrical Safety First / HSG85 standard",
    ],
    permits: [
      "Isolation and lock-off permit for each circuit worked dead",
      "Hot works permit if soldering or using a heat source",
      "Permit to drill/penetrate where the structure may contain hidden services",
    ],
    equipment: [
      "Lock-off kit with padlocks, hasps and caution tags",
      "Approved voltage indicator and proving unit to GS38",
      "Insulation resistance and continuity tester",
      "Cordless drill and SDS for fixings and chasing",
      "Cable rods, pulling lubricant and draw tape",
    ],
    pcChecks: [
      "That isolation is per-operative lock-off with proving dead at the point of work, not a single shared padlock at the board",
      "That energisation is a separate authorised event and is explicitly out of scope of the dead-working method",
      "That the BS 7671 inspection and test sign-off is allocated to a named, qualified person",
    ],
    faqs: [
      {
        q: "Do I need a separate permit to energise the new installation?",
        a: "Yes — energisation should be a controlled, authorised event kept distinct from the dead-working method covered here. The installation is initially verified by the dead-test sequence in BS 7671 (continuity, insulation resistance and polarity) before any supply is applied. Only once those results are satisfactory does a competent person remove the lock-off under a documented authorisation and carry out live testing such as earth fault loop impedance and RCD operation. Treating energisation separately stops anyone assuming the installation is dead once first fix is finished.",
      },
      {
        q: "Why does this RAMS not cover live fault-finding?",
        a: "Live fault-finding is a different activity with a different risk profile and is deliberately excluded so the method here can be a straightforward dead-working procedure. Working on or near live conductors is only justifiable under the three-condition test in regulation 14 of the Electricity at Work Regulations 1989, which installation work does not satisfy. If you also carry out live diagnostics, that work needs its own RAMS and a live-working permit. Keeping the documents separate keeps the installation method honest about how the work is actually done.",
      },
    ],
  },

  "elec-live-working": {
    whoFor:
      "Anyone who genuinely cannot avoid working on or near live electrical services needs this RAMS — and reviewers expect to see it justified, not assumed. It is written for the competent electrician and authorised person who must work adjacent to live conductors, switchgear or distribution equipment where isolation is not reasonably practicable. Principal contractors and client engineering teams will ask for it whenever your task touches a live board, and they will check that regulation 14 has actually been satisfied.",
    scope:
      "The scope covers tasks where conductors remain live because dead working is not reasonably practicable — for example fault diagnosis on an installation that cannot be shut down, or work immediately adjacent to live switchgear that cannot be isolated. The document sets out the regulation 14 justification, the precautions taken to prevent contact, and the supervision in place. It does not authorise routine live working as a convenience, and it does not cover normal dead installation work, which must be isolated. Each live task is subject to a specific permit naming the authorised person.",
    legislation: [
      "Electricity at Work Regulations 1989 (reg 14 — work on or near live conductors)",
      "Electricity at Work Regulations 1989 (reg 4 — systems and protective equipment)",
      "HSG85 Electricity at Work: Safe Working Practices",
      "GS38 Electrical test equipment for use by electricians",
      "Management of Health and Safety at Work Regulations 1999",
    ],
    sequence: [
      "Establish whether the work can be done dead; only proceed live where the regulation 14 three-condition test is satisfied and recorded.",
      "Issue a live-working permit naming the authorised person, the precise task and the duration.",
      "Define the live zone and fit insulating shrouding, barriers or insulated mats to prevent inadvertent contact with adjacent live parts.",
      "Brief an accompanying person trained in the task and in emergency rescue and resuscitation before work begins.",
      "Carry out the task using insulated tools and GS38-compliant test equipment, keeping one hand clear of earth where practicable.",
      "Test for danger, complete the task, then remove shrouding and barriers and close out the permit.",
    ],
    hazards: [
      {
        hazard: "Contact with live conductors causing electric shock or burns",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Justify and record the regulation 14 three-condition test before any live work",
          "Shroud or barrier off all adjacent live parts not being worked on",
          "Use insulated tools and a single-hand technique with the other hand kept clear of earth",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Arc flash and arc blast from a phase-to-earth or phase-to-phase fault",
        who: ["Operatives"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Assess prospective fault current and select arc-rated PPE to the calculated incident energy",
          "Maintain insulating barriers between phases and to earthed metalwork",
          "Restrict the number of people inside the arc-flash boundary to the task minimum",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Test probe slip or use of unfused test leads bridging live parts",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Use GS38-compliant probes with shrouded tips and fused, current-limited leads",
          "Verify the tester on a known live source before and after the dead/live check",
          "Apply finger barriers and minimise exposed probe length",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Lone working delaying rescue from contact or collapse",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Never work live alone — an accompanying person trained in emergency release must be present",
          "Confirm the means of isolation that the accompanying person can operate to break contact",
          "Ensure both persons hold current emergency first aid and CPR training",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Unauthorised or out-of-scope live work beyond what the permit allows",
        who: ["Operatives"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Issue a task-specific permit naming the authorised person and the precise equipment",
          "Prohibit any live activity not listed; stop and reissue if scope changes",
          "Cancel the permit and revert to dead working as soon as the live precaution is no longer required",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "Arc-rated face shield and balaclava to the assessed incident energy",
      "Arc-rated overalls or coverall",
      "Class 0 insulating gloves with leather over-protectors",
      "Insulating mat at the working position",
      "Safety footwear rated for electrical environments",
    ],
    competence: [
      "Authorised person status for live working under the company safety rules",
      "Demonstrated competence in the specific live task per HSG85",
      "GS38 test equipment and safe-working training",
      "Current emergency first aid including CPR for the working pair",
    ],
    permits: [
      "Live-working permit with the regulation 14 justification recorded",
      "Sanction-to-test where energised testing is involved",
      "Limitation-of-access permit for the area around the live zone",
    ],
    equipment: [
      "Insulating shrouding, phase barriers and insulated blankets",
      "Class 0 insulated tool set",
      "GS38-compliant voltage indicator with fused leads and proving unit",
      "Arc-flash boundary signage and exclusion barriers",
      "Insulating rescue hook for emergency release",
    ],
    pcChecks: [
      "That the regulation 14 three-condition justification is recorded for this specific task, not stated generically",
      "That an accompanying person trained in emergency rescue and CPR is named, with the isolation point they can operate",
      "That arc-rated PPE is selected against an assessed incident energy, not a generic clothing list",
    ],
    faqs: [
      {
        q: "Can I work on a live consumer unit because isolating it would be inconvenient?",
        a: "No — inconvenience is not a lawful reason to work live. Regulation 14 of the Electricity at Work Regulations 1989 only permits live working where it is unreasonable in all the circumstances for the conductor to be dead, it is reasonable in all the circumstances to work live, and suitable precautions are taken to prevent injury. All three conditions must be satisfied and recorded for the specific task before you begin. If you can shut the supply down to do the work safely, you must, and a dead-working RAMS applies instead.",
      },
      {
        q: "What test equipment is acceptable for proving dead near live parts?",
        a: "Use a two-pole voltage indicator that complies with GS38 — shrouded probe tips with no more than around 4 mm of exposed metal, finger barriers, and fused, current-limited leads. Prove the indicator on a known live source immediately before and after you check the conductor is dead, so you know the device was working. Avoid multimeters with long unfused leads for proving dead, as a slip can bridge live parts. The same GS38 standard applies whether you are proving dead or testing in a live environment.",
      },
    ],
  },

  "elec-temp-supply": {
    whoFor:
      "Site managers and electrical contractors setting up the power that keeps a construction site running need this RAMS. It is written for the person installing and maintaining the temporary distribution — the 110V reduced low voltage tools, the transformers, distribution boards and trailing leads that feed the whole site. When the principal contractor wants assurance that your temporary installation meets BS 7375 and is inspected on a regular regime, this is the document that demonstrates it.",
    scope:
      "The scope covers the design, installation, inspection and maintenance of a temporary electrical supply for the construction phase: the incoming supply or generator, distribution assemblies, RCD protection, 110V centre-tapped-to-earth circuits for portable tools, and the routing and mechanical protection of cables across the site. It covers the inspection regime that keeps the installation safe in a changing environment. It does not cover the permanent building installation, which is a separate dead-installation task, nor work on the DNO supply upstream of the site origin.",
    legislation: [
      "BS 7375:2010 Distribution of electricity on construction and demolition sites",
      "HSG141 Electrical safety on construction sites",
      "Electricity at Work Regulations 1989 (reg 4)",
      "BS 7671:2018+A2 Section 704 (construction site installations)",
      "Provision and Use of Work Equipment Regulations 1998",
    ],
    sequence: [
      "Plan the distribution from the supply origin or generator, sizing transformers and selecting 110V CTE for portable tools and lighting.",
      "Position weatherproof distribution assemblies with RCD protection on socket outlets, sited clear of traffic routes and water.",
      "Run and mechanically protect cables — bury, ramp or catenary leads so they cannot be crushed, cut or create trip hazards.",
      "Energise, carry out initial verification to BS 7671, and label boards with their inspection due dates.",
      "Operate a weekly visual inspection of distribution boards, leads and transformers, plus three-monthly periodic inspection and test.",
      "Adjust the distribution as the site develops, re-routing and re-testing affected circuits and recording each change.",
    ],
    hazards: [
      {
        hazard: "Shock from damaged 230V leads or tools where reduced low voltage is not used",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Use 110V centre-tapped-to-earth supplies for all portable tools and task lighting",
          "Where 230V is unavoidable, protect by 30 mA RCD and inspect leads weekly",
          "Remove damaged leads and tools from service immediately and tag them out",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Crushing or cutting of cables run across vehicle routes and through doorways",
        who: ["Operatives", "Public"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Route cables overhead on catenaries or bury and duct them clear of plant",
          "Use cable ramps at any unavoidable crossing of a traffic route",
          "Inspect protected runs weekly for damage and exposure",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Water ingress to distribution assemblies and connectors in wet site conditions",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Use IP-rated weatherproof assemblies and connectors raised clear of standing water",
          "Position boards under cover and keep lids and glands closed",
          "Include insulation resistance checks in the periodic inspection regime",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Trips over trailing leads and extension reels across walkways",
        who: ["Operatives", "Public"],
        initial: { likelihood: 4, severity: 2 },
        controls: [
          "Plan socket positions to minimise lead lengths across walkways",
          "Suspend or ramp leads that must cross pedestrian routes",
          "Fully unwind cable reels in use to prevent overheating and tidy them when finished",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Generator fumes and fuel handling where a mobile generator feeds the site",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Site generators in the open away from occupied spaces and air intakes",
          "Bund fuel storage and refuel only when the generator is stopped and cool",
          "Earth the generator and protect outgoing circuits with RCDs",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Safety footwear suitable for wet site conditions",
      "Waterproof gloves for handling outdoor connectors",
      "High-visibility clothing for work near traffic routes",
      "Head protection on the active construction site",
      "Eye protection for cable cutting and gland work",
    ],
    competence: [
      "ECS card with the relevant electrical grade",
      "Familiarity with BS 7375 and HSG141 site distribution requirements",
      "Inspection and testing competence for the periodic regime",
      "Awareness of generator earthing and CTE 110V system principles",
    ],
    permits: [
      "Permit for excavation where cables are buried across the site",
      "Hot works permit if termination involves a heat source",
      "Authorisation to alter the temporary distribution layout",
    ],
    equipment: [
      "110V site transformers and distribution units",
      "Weatherproof RCD-protected distribution boards",
      "Cable ramps, catenary wire and ducting for lead protection",
      "Multifunction installation tester for periodic verification",
      "Lockable enclosures and weatherproof connectors",
    ],
    pcChecks: [
      "That an inspection regime is defined — weekly visual of distribution boards and leads plus three-monthly periodic inspection and test",
      "That portable tools and task lighting are fed from 110V centre-tapped-to-earth, not 230V, across the site",
      "That cable routing across traffic and pedestrian routes is mechanically protected, not just laid on the ground",
    ],
    faqs: [
      {
        q: "Why do construction sites use 110V rather than 230V for tools?",
        a: "BS 7375 and HSG141 recommend reduced low voltage — 110V centre-tapped-to-earth — for portable tools and task lighting on construction sites. With a CTE system the voltage to earth from either conductor is only 55V, which greatly reduces the severity of any shock if a lead is damaged in the harsh site environment. Damaged leads and water ingress are far more likely on a building site than in a finished installation, so the lower voltage is a practical extra layer of protection. Where 230V genuinely cannot be avoided, it must be protected by a 30 mA RCD and inspected frequently.",
      },
      {
        q: "How often must the temporary installation be inspected?",
        a: "The temporary installation should have a weekly visual inspection of distribution boards, transformers and leads, plus a periodic inspection and test typically at three-monthly intervals because the site environment changes constantly. Boards should be labelled with their next due date so anyone can see at a glance whether the regime is current. Any board or lead found damaged is taken out of service and tagged immediately rather than left for the next periodic. The RamsDocs builder lets you record the inspection frequency and the responsible person directly in the document so the regime is part of the RAMS, not a loose schedule.",
      },
    ],
  },

  "elec-overhead-lines": {
    whoFor:
      "This RAMS is for groundworks, plant and civils teams working on a site crossed or bordered by overhead power lines — not for electricians. It is written for the site agent, plant operators and banksman who must keep machines, tipping bodies and materials a safe distance from live conductors. Principal contractors and the network operator will expect to see a GS6-based control plan before any plant moves near the lines, and this document provides it.",
    scope:
      "The scope covers all site activity carried out beneath or adjacent to overhead power lines: excavation, muck-away, craneage, scaffolding, tipping and the movement of high plant within reach of the conductors. It sets out the consultation with the network operator, the exclusion distances and physical goalposts and barriers that keep plant clear, and the banksman supervision controlling each pass. It does not cover any work on the lines themselves or their diversion, which is carried out by the distribution network operator under a separate arrangement.",
    legislation: [
      "GS6 Avoiding danger from overhead power lines",
      "HSG144 The safe use of vehicles on construction sites",
      "Construction (Design and Management) Regulations 2015",
      "Electricity at Work Regulations 1989 (reg 14)",
      "Provision and Use of Work Equipment Regulations 1998",
    ],
    sequence: [
      "Confirm the route, voltage and owner of every overhead line with the distribution network operator before any plant comes to site.",
      "Agree clearance and exclusion distances with the network operator using GS6, allowing for conductor sway and plant reach.",
      "Erect goalposts at defined passage points and continuous barriers along the no-go zone beneath the conductors.",
      "Brief plant operators and banksmen on the exclusion zone, the permitted crossing points and the prohibition on raising bodies near the lines.",
      "Control every approach with a dedicated banksman; no high plant enters the zone without banksman supervision.",
      "Keep the goalposts and barriers in place for the duration of the works and remove them only once all plant has finished beneath the lines.",
    ],
    hazards: [
      {
        hazard: "Plant or raised body contacting or flashing over to a live overhead conductor",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Establish GS6 exclusion distances agreed with the network operator before work starts",
          "Erect goalposts and barriers to define safe passage and the no-go zone",
          "Prohibit raising tipping bodies, jibs or scaffold tubes within the exclusion zone",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Crane or excavator jib swinging into the conductor zone",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Position craneage so the jib cannot reach the exclusion zone at any slew angle",
          "Fit and set slew or height limiters where the machine supports them",
          "Control every lift with an appointed person and a dedicated banksman",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Tipper or telehandler raising a load above the safe passage height at the goalposts",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Restrict crossings to the goalposted passage points only",
          "Set the goalpost height below the GS6 clearance and signpost the limit clearly",
          "Brief drivers never to tip or raise a body while beneath or near the lines",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Step and touch potential if a machine does contact a line, injuring those nearby",
        who: ["Operatives", "Public"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Instruct operators to stay in the cab if contact occurs and drive clear if possible",
          "Keep all other persons well back from a machine in contact until the line is confirmed dead",
          "Treat the line as live until the network operator confirms otherwise",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Scaffolding or long materials carried up into the conductor zone",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Plan material storage and scaffold erection outside the exclusion zone",
          "Carry long items horizontally and brief teams on the overhead hazard",
          "Maintain barriers so the zone remains visibly defined throughout",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "High-visibility clothing for all plant and banksman activity",
      "Head protection on the construction site",
      "Safety footwear with toe protection",
      "Gloves for handling barrier and goalpost components",
      "Eye protection where ground breaking generates debris",
    ],
    competence: [
      "Trained and certificated plant operators (CPCS/NPORS) for the machines in use",
      "Appointed banksman/signaller trained in the GS6 control plan",
      "Site agent familiar with GS6 and the agreed exclusion distances",
      "Briefing records confirming all operators understand the no-go zone",
    ],
    permits: [
      "Permit to work near overhead lines authorising each activity in the zone",
      "Network operator consent for any line shrouding or diversion",
      "Excavation permit where digging is carried out beneath the lines",
    ],
    equipment: [
      "Goalposts and bunting marking passage height and the conductor zone",
      "Rigid barriers and warning signage defining the no-go area",
      "Height-limited or limiter-fitted plant for work near the lines",
      "Banksman radios for controlling each approach",
      "Survey tape and markers to set out the agreed exclusion distances",
    ],
    pcChecks: [
      "That the line voltage and owner have been confirmed with the network operator and the GS6 exclusion distances recorded",
      "That physical controls — goalposts, barriers and signage — are specified, not just a written instruction to keep clear",
      "That a dedicated banksman controls every plant approach, with the prohibition on raising bodies stated explicitly",
    ],
    faqs: [
      {
        q: "What is the exclusion zone for an 11kV overhead line?",
        a: "For overhead lines up to and including 33kV, GS6 recommends a minimum clearance of 3 metres from the conductors, so the exclusion zone for an 11kV line is set at least 3 metres out, with goalposts and barriers positioned to keep plant outside it. The distance must allow for conductor sway in the wind and for the full reach of the plant, so it is often set wider in practice. You should always confirm the actual voltage and the required clearance with the distribution network operator before work starts rather than assuming a figure. Where safe distances cannot be maintained, the line may need to be diverted or made dead by the network operator.",
      },
      {
        q: "Do I need to contact the network operator before working near their lines?",
        a: "Yes — GS6 expects you to identify the lines on or near the site and consult the distribution network operator who owns them at the planning stage. They confirm the voltage, the required clearance distances and whether the line can be diverted, shrouded or made dead for the duration of the works. Treat every overhead line as live and dangerous until the operator tells you otherwise in writing. Their advice shapes the goalpost heights, barrier positions and exclusion distances that the rest of the control plan is built around.",
      },
    ],
  },
};
